import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, Database } from '../lib/supabase'
import {
  ArrowLeftIcon,
  PhotoIcon,
  ArchiveBoxIcon,
  TagIcon,
  CalendarIcon,
  PencilIcon,
  CameraIcon,
} from '@heroicons/react/24/outline'

type CookieCutter = Database['public']['Tables']['cookie_cutters']['Row'] & {
  storage_bins?: Database['public']['Tables']['storage_bins']['Row'] | null
}

export default function CookieCutterDetail() {
  const [cookieCutter, setCookieCutter] = useState<CookieCutter | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState('')
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const fetchCookieCutter = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('cookie_cutters')
        .select(`
          *,
          storage_bins (
            id,
            name,
            created_at
          )
        `)
        .eq('id', id)
        .eq('user_id', user?.id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          setError('Cookie cutter not found')
        } else {
          console.error('Error fetching cookie cutter:', error)
          setError('Failed to load cookie cutter details')
        }
      } else {
        setCookieCutter(data)
      }
    } catch (err) {
      console.error('Error:', err)
      setError('Failed to load cookie cutter details')
    } finally {
      setLoading(false)
    }
  }, [id, user?.id])

  useEffect(() => {
    if (id && user) {
      fetchCookieCutter()
    }
  }, [id, user, fetchCookieCutter])

  const handleQuickPhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !cookieCutter) return

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB')
      return
    }

    setUploadingPhoto(true)
    setError('')
    setUploadSuccess('')

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `cookie_cutter_${cookieCutter.id}_${Date.now()}.${fileExt}`
      const filePath = `${user?.id}/${fileName}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('cookie-cutters')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('cookie-cutters')
        .getPublicUrl(filePath)

      // Update cookie cutter record
      const { error: updateError } = await supabase
        .from('cookie_cutters')
        .update({ photo_url: urlData.publicUrl })
        .eq('id', cookieCutter.id)
        .eq('user_id', user?.id)

      if (updateError) throw updateError

      // Update local state
      setCookieCutter({ ...cookieCutter, photo_url: urlData.publicUrl })
      setUploadSuccess('Photo updated successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(''), 3000)

    } catch (err) {
      console.error('Error uploading photo:', err)
      setError('Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      // Reset file input
      event.target.value = ''
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-600">Loading cookie cutter details...</div>
        </div>
      </div>
    )
  }

  if (error || !cookieCutter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Cookie cutter not found'}
          </h2>
          <p className="text-gray-600 mb-4">
            The cookie cutter you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Link
            to="/inventory"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Inventory
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <nav className="flex items-center text-sm text-gray-500 mb-4">
          <Link to="/inventory" className="hover:text-gray-700 flex items-center">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Inventory
          </Link>
        </nav>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{cookieCutter.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Added on {new Date(cookieCutter.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={() => navigate(`/inventory/edit/${cookieCutter.id}`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      {uploadSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-800">{uploadSuccess}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Photo */}
        <div className="space-y-4">
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group">
            {uploadingPhoto && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm">Uploading...</p>
                </div>
              </div>
            )}
            
            {cookieCutter.photo_url ? (
              <img
                src={cookieCutter.photo_url}
                alt={cookieCutter.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <PhotoIcon className="h-24 w-24 text-gray-400" />
              </div>
            )}
            
            {/* Quick photo upload buttons */}
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
              <label
                className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200 cursor-pointer inline-flex items-center"
                title="Quick photo upload"
              >
                <CameraIcon className="h-4 w-4" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleQuickPhotoUpload}
                  disabled={uploadingPhoto}
                />
              </label>
              <Link
                to={`/inventory/edit/${cookieCutter.id}`}
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200 inline-flex items-center"
                title="Edit details"
              >
                <PencilIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <p className="text-sm text-gray-500 text-center">
            Hover over photo to upload or edit
          </p>
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  <TagIcon className="h-4 w-4 mr-2" />
                  Category
                </dt>
                <dd className="mt-1 ml-6">
                  {cookieCutter.category ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {cookieCutter.category}
                    </span>
                  ) : (
                    <span className="text-gray-400">Uncategorized</span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                  Storage Bin
                </dt>
                <dd className="mt-1 ml-6">
                  {cookieCutter.storage_bins ? (
                    <Link
                      to={`/storage-bins/${cookieCutter.storage_bins.id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {cookieCutter.storage_bins.name}
                    </Link>
                  ) : (
                    <span className="text-gray-400">Unassigned</span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="flex items-center text-sm font-medium text-gray-500">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Date Added
                </dt>
                <dd className="mt-1 ml-6 text-sm text-gray-900">
                  {new Date(cookieCutter.created_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </dd>
              </div>
            </dl>
          </div>

          {cookieCutter.description && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{cookieCutter.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}